import React, { useState, useEffect } from 'react';
import { useNavigation } from 'context/NavigationContext';
import {
    BringzzPageContainer,
    BringzzButton,
    BringzzIcon,
    BringzzText,
    BringzzInput,
    BringzzRotatingSelect,
    BringzzBottomDrawer,
    BringzzCheckbox
} from '@bringzz/components';
import useUser from 'hooks/useUser';
import useRequest from 'hooks/useRequest';

const EditPharmacyOpeningHours = () => {
    const { backPress, navigate } = useNavigation();
    const { sendRequest, loading } = useRequest();
    const { getUser } = useUser();
    const [currentHours, setCurrentHours] = useState(null);
    const [currentMinutes, setCurrentMinutes] = useState(null);

    const [selectingTime, setSelectingTime] = useState(false);
    const [selectingOpeningHours, setSelectingOpeningHours] = useState(false);
    const [selectingClosingHours, setSelectingClosingHours] = useState(false);
    const [selectingDayIndex, setSelectingDayIndex] = useState(null);
    const [selectingHoursIntervalIndex, setSelectingHoursIntervalIndex] =
        useState(null);
    const [timeError, setTimeError] = useState('');

    const [openingHours, setOpeningHours] = useState([
        {
            closed: false,
            hours: [{ opens: '', closes: '' }],
            dayName: 'Monday'
        },
        {
            closed: false,
            hours: [{ opens: '', closes: '' }],
            dayName: 'Tuesday'
        },
        {
            closed: false,
            hours: [{ opens: '', closes: '' }],
            dayName: 'Wednesday'
        },
        {
            closed: false,
            hours: [{ opens: '', closes: '' }],
            dayName: 'Thursday'
        },
        {
            closed: false,
            hours: [{ opens: '', closes: '' }],
            dayName: 'Friday'
        },
        {
            closed: true,
            hours: [{ opens: '', closes: '' }],
            dayName: 'Saturday'
        },
        {
            closed: true,
            hours: [{ opens: '', closes: '' }],
            dayName: 'Sunday'
        }
    ]);

    const hoursOptions = Array.from({ length: 24 }, (_, i) => ({ label: i.toString().padStart(2, '0'), value: i }));

    const generateMinutesOptions = () => {
        let array = [];
        for (let index = 0; index < 60; index++) {
            array.push({ value: index, label: index.toString().padStart(2, '0') });
        }
        return array;
    };
    const minutesOptions = generateMinutesOptions();
    const changeHours = result => {
        setCurrentHours(result.value);
        validateTime(result.value, currentMinutes);
    };
    const changeMinutes = result => {
        setCurrentMinutes(result.value);
        validateTime(currentHours, result.value);
    };

    const validateTime = (hours, minutes) => {
        if (hours === null || minutes === null) return;
        const time = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        
        if (selectingClosingHours) {
            const openTime = openingHours[selectingDayIndex].hours[selectingHoursIntervalIndex].opens;
            if (openTime && time <= openTime) {
                setTimeError('Closing time must be after opening time');
            } else {
                setTimeError('');
            }
        } else {
            const closeTime = openingHours[selectingDayIndex].hours[selectingHoursIntervalIndex].closes;
            if (closeTime && time >= closeTime) {
                setTimeError('Opening time must be before closing time');
            } else {
                setTimeError('');
            }
        }
    };

    const closeTimeSelection = () => {
        if (timeError) return; // Don't close if there's an error
        
        const hours = currentHours !== null ? currentHours : 0;
        const minutes = currentMinutes !== null ? currentMinutes : 0;
        const time = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        const openingHoursCopy = [...openingHours];
        
        if (selectingClosingHours) {
          openingHoursCopy[selectingDayIndex].hours[selectingHoursIntervalIndex].closes = time;
        } else {
          openingHoursCopy[selectingDayIndex].hours[selectingHoursIntervalIndex].opens = time;
        }
        
        setOpeningHours(openingHoursCopy);
        setSelectingTime(false);
        setSelectingOpeningHours(false);
        setSelectingClosingHours(false);
        setCurrentHours(null);
        setCurrentMinutes(null);
        setTimeError('');
      };

    const addInterval = dayIndex => {
        const openingHoursCopy = [...openingHours];
        openingHoursCopy[dayIndex].hours.push({ opens: '', closes: '' });
        setOpeningHours(openingHoursCopy);
    };
    const removeInterval = (dayIndex, intervalIndex) => {
        const openingHoursCopy = [...openingHours];
        if (openingHoursCopy[dayIndex].hours.length > 1) {
            openingHoursCopy[dayIndex].hours.splice(intervalIndex, 1);
            setOpeningHours(openingHoursCopy);
        }
    };

    const changeClosedState = (index, value) => {
        const openingHoursCopy = [...openingHours];
        openingHoursCopy[index].closed = value;
        openingHoursCopy[index].isOpen = value ? 0 : 1;

        if (value) {
            openingHoursCopy[index].hours = [];
        } else {
            if (openingHoursCopy[index].hours.length === 0) {
                openingHoursCopy[index].hours.push({ opens: '', closes: '' });
            }
        }

        setOpeningHours(openingHoursCopy);
    };

    useEffect(() => {
        const fetchOpeningHours = async () => {
            try {
                const response = await sendRequest({
                    method: 'GET',
                    url: '/module/pharmacyopening/getPharmacyOpeningHours'
                });
                if (response?.status === 'success') {
                    const transformedData = transformBackendData(response.data);
                    setOpeningHours(transformedData);
                }
            } catch (error) {
                console.error('Failed to fetch opening hours:', error);
            }
        };

        fetchOpeningHours();
    }, [sendRequest]);

    const transformBackendData = (backendData) => {
        const days = Array.from({ length: 7 }, (_, index) => ({
            closed: false,
            hours: [{ opens: '', closes: '' }],
            dayName: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][index]
        }));

        if (backendData && backendData.length > 0) {
            backendData.forEach((entry) => {
                const dayIndex = entry.day - 1;
                if (dayIndex < 0 || dayIndex >= 7) return;

                if (entry.isOpen === 0) {
                    days[dayIndex].closed = true;
                    days[dayIndex].hours = [];
                } else if (entry.isOpen === 1) {
                    days[dayIndex].closed = false;
                    let starts, ends;
                    try {
                        starts = JSON.parse(entry.start);
                        ends = JSON.parse(entry.end);
                    } catch (e) {
                        starts = [entry.start];
                        ends = [entry.end];
                    }
                    
                    if (!Array.isArray(starts)) starts = [starts];
                    if (!Array.isArray(ends)) ends = [ends];
                    
                    days[dayIndex].hours = [];
                    for (let i = 0; i < Math.max(starts.length, ends.length); i++) {
                        days[dayIndex].hours.push({
                            opens: starts[i] || '',
                            closes: ends[i] || ''
                        });
                    }
                }
            });
        }

        return days;
    };

    const convertTo24Hour = (time12h) => {
        if (!time12h) return '00:00';
        const [time, modifier] = time12h.split(' ');
        let [hours, minutes] = time.split(':');
        hours = parseInt(hours, 10);
        return `${hours.toString().padStart(2, '0')}:${minutes || '00'}`;
    };

    const convertToBackendFormat = (hoursData) => {
        const result = [];
        hoursData.forEach((day, index) => {
            const backendDay = index + 1;
            
            if (day.closed) {
                result.push({
                    day: backendDay,
                    start: ['00:00'],
                    end: ['23:59'],
                    isOpen: 0
                });
            } else {
                const starts = [];
                const ends = [];
                
                day.hours.forEach(interval => {
                    if (interval.opens && interval.closes) {
                        starts.push(convertTo24Hour(interval.opens));
                        ends.push(convertTo24Hour(interval.closes));
                    }
                });
                
                if (starts.length > 0) {
                    result.push({
                        day: backendDay,
                        start: starts,
                        end: ends,
                        isOpen: 1
                    });
                }
            }
        });
        return result;
    };

    return (
        <BringzzPageContainer
            header={{
                left: (
                    <BringzzIcon
                        size='22'
                        icon='IconChevronLeft'
                        folder='LineIcons'
                        className='cursor-pointer'
                        onClick={backPress}
                    />
                ),
                title: <BringzzText tag='h2'>Opening hours</BringzzText>
            }}
            footer={
                <div className='pb-28 px-6'>
                    <BringzzButton
                        size='lg'
                        className='bg-magic-lilac w-full flex justify-center text-midnight-blue'
                        onClick={async () => {
                            const payload = convertToBackendFormat(openingHours);
                            await sendRequest({
                                method: 'POST',
                                url: `/module/pharmacyopening/updatePharmacyOpeningHours`,
                                data: { data: payload }
                            })
                                .then(response => {
                                    if (response.status === 'success')
                                        navigate(`/settings/pharmacy/info`, false);
                                })
                                .catch(error => {
                                    console.log(error);
                                });
                        }}
                        disabled={loading}
                    >
                        Save
                    </BringzzButton>
                </div>
            }
        >
            <div className='p-2 space-y-4'>
                {selectingTime && (
                    <BringzzBottomDrawer
                        isOpen={selectingTime}
                        close={closeTimeSelection}
                        title='Select...'
                    >
                        <div className='flex items-center relative gap-x-4 justify-center'>
                            <div className='h-12 absolute top-1/2 right-0 w-full border-y -translate-y-1/2'></div>
                            <div>
                                <BringzzRotatingSelect
                                    options={hoursOptions}
                                    onSelect={changeHours}
                                    defaultOption={currentHours}
                                />
                            </div>
                            <div>
                                <BringzzRotatingSelect
                                    options={minutesOptions}
                                    onSelect={changeMinutes}
                                    defaultOption={currentMinutes}
                                />
                            </div>
                        </div>

                        {timeError && (
                            <div className='flex justify-center mt-2'>
                                <BringzzText className='text-red-500 text-sm'>{timeError}</BringzzText>
                            </div>
                        )}

                        <div className='flex justify-center items-center mt-4'>
                            <BringzzButton
                                className={timeError ? 'bg-gray-300' : 'bg-magic-lilac'}
                                onClick={closeTimeSelection}
                                disabled={!!timeError}
                            >
                                Apply
                            </BringzzButton>
                        </div>
                    </BringzzBottomDrawer>
                )}
                <div className='relative flex justify-center'>
                    <div className='relative bg-white w-full bg-cover bg-center flex flex-col'>
                        {/* Content */}
                        <div className='flex-grow flex flex-col px-4'>
                            <div className='z-10 flex flex-col '>
                                {openingHours &&
                                    openingHours.map((day, dayIndex) => {
                                        return (
                                            <div
                                                className='flex gap-x-6 mt-4 pb-4 border-b'
                                                key={dayIndex}
                                            >
                                                <div className='grow'>
                                                    <div>
                                                        <BringzzText>{day.dayName}</BringzzText>
                                                    </div>
                                                    <div className='mt-3'>
                                                        <BringzzCheckbox
                                                            label='closed'
                                                            onChange={value => {
                                                                changeClosedState(dayIndex, value);
                                                            }}
                                                            checked={day.closed}
                                                        ></BringzzCheckbox>
                                                    </div>
                                                </div>
                                                <div className='space-y-4'>
                                                    {!day.closed &&
                                                        day.hours.map((interval, intervalIndex) => {
                                                            return (
                                                                <div
                                                                    className='flex items-center gap-x-4 '
                                                                    key={intervalIndex}
                                                                >
                                                                    <div className=''>
                                                                        <div>
                                                                            <BringzzText>Opens</BringzzText>
                                                                        </div>
                                                                        <div className='w-[80px]'>
                                                                            <BringzzInput
                                                                                type='text'
                                                                                placeholder='HH:MM'
                                                                                onClick={() => {
                                                                                    // Parse existing time if available
                                                                                    if (interval.opens) {
                                                                                        const [hours, minutes] = interval.opens.split(':');
                                                                                        setCurrentHours(parseInt(hours, 10));
                                                                                        setCurrentMinutes(parseInt(minutes, 10));
                                                                                    } else {
                                                                                        setCurrentHours(null);
                                                                                        setCurrentMinutes(null);
                                                                                    }
                                                                                    setSelectingOpeningHours(true);
                                                                                    setSelectingDayIndex(dayIndex);
                                                                                    setSelectingHoursIntervalIndex(
                                                                                        intervalIndex
                                                                                    );
                                                                                    setSelectingTime(true);
                                                                                }}
                                                                                value={interval.opens}
                                                                                readOnly
                                                                            />{' '}
                                                                        </div>
                                                                    </div>
                                                                    <div className=''>
                                                                        <div>
                                                                            <BringzzText>Closes</BringzzText>
                                                                        </div>
                                                                        <div className=' flex items-center gap-x-4'>
                                                                            <div className='w-[80px]'>
                                                                                <BringzzInput
                                                                                    type='text'
                                                                                    placeholder='HH:MM'
                                                                                    onClick={() => {
                                                                                        // Parse existing time if available
                                                                                        if (interval.closes) {
                                                                                            const [hours, minutes] = interval.closes.split(':');
                                                                                            setCurrentHours(parseInt(hours, 10));
                                                                                            setCurrentMinutes(parseInt(minutes, 10));
                                                                                        } else {
                                                                                            setCurrentHours(null);
                                                                                            setCurrentMinutes(null);
                                                                                        }
                                                                                        setSelectingClosingHours(true);
                                                                                        setSelectingDayIndex(dayIndex);
                                                                                        setSelectingHoursIntervalIndex(
                                                                                            intervalIndex
                                                                                        );
                                                                                        setSelectingTime(true);
                                                                                    }}
                                                                                    value={interval.closes || ''}
                                                                                    readOnly
                                                                                />
                                                                            </div>

                                                                            <div>
                                                                                {intervalIndex === 0 ? (
                                                                                    <BringzzIcon
                                                                                        size='28'
                                                                                        icon='IconCirclePlus'
                                                                                        folder='LineIcons'
                                                                                        className='cursor-pointer'
                                                                                        onClick={() => {
                                                                                            addInterval(dayIndex);
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    <BringzzIcon
                                                                                        size='28'
                                                                                        icon='IconTrashCan'
                                                                                        folder='LineIcons'
                                                                                        className='cursor-pointer'
                                                                                        onClick={() => {
                                                                                            removeInterval(dayIndex, intervalIndex);
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </BringzzPageContainer>
    );
};

export default EditPharmacyOpeningHours;